// Live workflow-activity SignalR connection.
//
// One connection per authenticated session. Mounted INSIDE RequireAuth (see
// routes.tsx) so it only ever connects when signed in, and tears down on
// sign-out / unmount. Components subscribe via the useActivity() hook and
// register a handler that fires only while they're mounted — so a SignalR
// notice never renders on a page that didn't ask for it.
//
// Routing: the server adds this connection to its own `tenant:{id}` group on
// connect (derived from the auth cookie, never a client parameter), so the
// tenant-wide feed needs no client-side subscribe call. Detail pages can call
// subscribeWorkflow(id) to also join the per-workflow group.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import type { AiGenerationProgressEvent, WorkflowActivityEvent } from '@shared/api-types';
import { ActivityContext, type ActivityContextValue } from './activity';

/** Server-side SendAsync event name (matches SignalRWorkflowActivityNotifier.EventName). */
const ACTIVITY_EVENT = 'WorkflowActivity';
/** Server-side SendAsync event name (matches SignalRAiGenerationProgressNotifier.EventName). */
const AI_PROGRESS_EVENT = 'AiGenerationProgress';
/** Same-origin hub path — under /api/* so Front Door + the dev proxy route it. */
const HUB_URL = '/api/hubs/activity';

export function ActivityConnectionProvider({ children }: { children: ReactNode }) {
  const connectionRef = useRef<HubConnection | null>(null);
  const [connected, setConnected] = useState(false);
  // Fan-out set of subscribers registered via onWorkflowActivity().
  const handlersRef = useRef(new Set<(e: WorkflowActivityEvent) => void>());
  // Fan-out set of subscribers registered via onAiGenerationProgress().
  const aiProgressHandlersRef = useRef(new Set<(e: AiGenerationProgressEvent) => void>());

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    connectionRef.current = connection;

    connection.on(ACTIVITY_EVENT, (e: WorkflowActivityEvent) => {
      handlersRef.current.forEach((h) => h(e));
    });
    connection.on(AI_PROGRESS_EVENT, (e: AiGenerationProgressEvent) => {
      aiProgressHandlersRef.current.forEach((h) => h(e));
    });
    connection.onreconnected(() => setConnected(true));
    connection.onclose(() => setConnected(false));

    let cancelled = false;
    // Capture the start promise so cleanup can wait for it to SETTLE before
    // stopping. Calling stop() while negotiation is still in flight rejects
    // start() with "The connection was stopped during negotiation" — which is
    // exactly what React StrictMode's mount→cleanup→mount cycle triggers in dev.
    const startPromise = connection
      .start()
      .then(() => {
        if (!cancelled) setConnected(true);
      })
      .catch(() => {
        // Best-effort: the dashboard still renders without the live feed.
        if (!cancelled) setConnected(false);
      });

    return () => {
      cancelled = true;
      connection.off(ACTIVITY_EVENT);
      connection.off(AI_PROGRESS_EVENT);
      // Defer stop() until the start attempt has settled so we never abort an
      // in-flight negotiation (no spurious StrictMode error; clean teardown).
      void startPromise.finally(() => {
        void connection.stop();
      });
      connectionRef.current = null;
    };
  }, []);

  const onWorkflowActivity = useCallback((handler: (e: WorkflowActivityEvent) => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const onAiGenerationProgress = useCallback((handler: (e: AiGenerationProgressEvent) => void) => {
    aiProgressHandlersRef.current.add(handler);
    return () => {
      aiProgressHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeWorkflow = useCallback(async (workflowId: string) => {
    const c = connectionRef.current;
    if (c?.state === HubConnectionState.Connected) {
      await c.invoke('SubscribeWorkflow', workflowId);
    }
  }, []);

  const unsubscribeWorkflow = useCallback(async (workflowId: string) => {
    const c = connectionRef.current;
    if (c?.state === HubConnectionState.Connected) {
      await c.invoke('UnsubscribeWorkflow', workflowId);
    }
  }, []);

  const value = useMemo<ActivityContextValue>(
    () => ({
      connected,
      onWorkflowActivity,
      onAiGenerationProgress,
      subscribeWorkflow,
      unsubscribeWorkflow,
    }),
    [connected, onWorkflowActivity, onAiGenerationProgress, subscribeWorkflow, unsubscribeWorkflow],
  );

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}
